<script lang="ts">
  import type { Snippet } from "svelte";
  import { focusTrap } from "../a11y/focusTrap";
  import { lockScroll } from "../a11y/scrollLock";

  interface Props {
    open: boolean;
    title: string;
    description?: string;
    /** Set false to require an explicit action (e.g. destructive confirms). */
    dismissable?: boolean;
    onclose: () => void;
    children: Snippet;
    footer?: Snippet;
  }
  const {
    open,
    title,
    description,
    dismissable = true,
    onclose,
    children,
    footer
  }: Props = $props();

  const titleId = "dialog-title-" + Math.random().toString(36).slice(2, 8);
  const descId = "dialog-desc-" + Math.random().toString(36).slice(2, 8);

  $effect(() => {
    if (open) return lockScroll();
    return undefined;
  });

  function onOverlayClick(event: MouseEvent): void {
    if (dismissable && event.target === event.currentTarget) onclose();
  }
</script>

{#if open}
  <div class="overlay" onclick={onOverlayClick} role="presentation">
    <div
      class="panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      use:focusTrap={{ onEscape: dismissable ? onclose : undefined }}
    >
      <header class="panel-head">
        <div>
          <h2 id={titleId} class="panel-title">{title}</h2>
          {#if description}<p id={descId} class="panel-desc">{description}</p>{/if}
        </div>
        <!--
          Stays MOUNTED and goes disabled instead of unmounting. Ten callers pass
          `dismissable={!busy}`, so unmounting it made the header reflow the moment a
          submit started — the panel visibly twitched under the user's cursor at the
          least reassuring possible moment. `data-focus-skip` keeps the dialog's
          initial focus off it without changing Tab order.
        -->
        <button
          type="button"
          class="panel-close"
          onclick={onclose}
          disabled={!dismissable}
          aria-label="Close dialog"
          data-focus-skip
        >
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
        </button>
      </header>
      <div class="panel-body">{@render children()}</div>
      {#if footer}<footer class="panel-foot">{@render footer()}</footer>{/if}
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: grid;
    padding: 20px;
    background: var(--scrim);
    backdrop-filter: blur(2px);
    overflow-y: auto;
  }
  .panel {
    /* Auto margins center like place-items:center but keep the top edge
     * reachable when the panel is taller than the viewport (overflow-y). */
    margin: auto;
    width: 100%;
    max-width: 520px;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    padding: 24px;
    box-shadow: var(--shadow-xl);
    animation: dialog-in var(--dur-enter) var(--ease-out);
  }
  @media (prefers-reduced-motion: reduce) {
    .panel {
      animation: none;
    }
  }
  @keyframes dialog-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
  }
  .panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }
  .panel-title {
    font-size: 18px;
    line-height: 1.25;
  }
  .panel-desc {
    color: var(--muted);
    font-size: 14px;
    margin: 6px 0 0;
  }
  .panel-close:disabled {
    opacity: 0.55;
    cursor: default;
  }
  .panel-close {
    flex: none;
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    border: 1px solid var(--line-soft);
    background: transparent;
    color: var(--muted);
    cursor: pointer;
  }
  .panel-close:hover {
    color: var(--heading);
    border-color: var(--accent);
    background: var(--accent-weak);
  }
  .panel-close svg {
    width: 16px;
    height: 16px;
  }
  .panel-foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 22px;
    flex-wrap: wrap;
  }
</style>
